import { useState } from 'react';
import type { LoanEffectivePolicy, LoanFinancialSnapshotV1 } from './loan-financial.types';
import { buildAccountingSummaryRows } from './loan-financial.view-model';
import {
  allocationOrderLabel,
  interestRecognitionLabel,
  payoffPolicyLabel,
} from './loan-financial.labels';
import { Hint, MoneyRow, SectionCard, SkeletonBlock } from './LoanUiBits';
import { formatDateTimeAr } from './loan-financial.format';

type Props = {
  snap: LoanFinancialSnapshotV1 | null;
  policy: LoanEffectivePolicy | null;
  policyLoading?: boolean;
  auditorMode?: boolean;
};

export function LoanAccountingSummary({ snap, policy, policyLoading, auditorMode }: Props) {
  const [policyOpen, setPolicyOpen] = useState(false);

  if (!snap) {
    return <SkeletonBlock className="h-40" />;
  }

  const rows = buildAccountingSummaryRows(snap);

  return (
    <div className="space-y-4">
      <SectionCard
        title="Saldo contable (cuentas por cobrar)"
        subtitle="Importes reconocidos en el mayor atribuidos al préstamo."
      >
        <div className="space-y-1.5">
          {rows.map((r) => (
            <MoneyRow
              key={r.label}
              label={r.label}
              value={r.value}
              emphasis={r.label === 'Saldo contable'}
              tooltip={r.tooltip}
            />
          ))}
        </div>
        <p className="mt-3 text-xs text-slate-500 leading-relaxed">
          El saldo contable puede ser diferente del total contractual porque los intereses y cargos
          pueden reconocerse contablemente en otro momento según la política del préstamo.
          <Hint text="El saldo contractual representa lo pendiente del plan de pagos, incluyendo conceptos futuros. El saldo contable representa los importes actualmente reconocidos en las cuentas por cobrar." />
        </p>
      </SectionCard>

      <SectionCard
        title="Política del préstamo"
        actions={
          <button
            type="button"
            className="text-xs text-blue-700 hover:underline"
            onClick={() => setPolicyOpen((v) => !v)}
          >
            {policyOpen ? 'Ocultar detalle' : 'Ver detalle'}
          </button>
        }
      >
        {policyLoading && !policy ? (
          <SkeletonBlock className="h-16" />
        ) : (
          <div className="space-y-2 text-sm text-slate-700">
            <p>
              Reconocimiento de intereses:{' '}
              <strong>
                {interestRecognitionLabel(
                  policy?.interestRecognitionMethod ?? snap.interestRecognitionMethod,
                )}
              </strong>
            </p>
            <p>
              Orden de imputación:{' '}
              <strong>{allocationOrderLabel(policy?.paymentAllocationOrder)}</strong>
            </p>
            <p>
              Política de cancelación:{' '}
              <strong>{payoffPolicyLabel(policy?.payoffPolicy ?? snap.payoff?.policy)}</strong>
            </p>
            {policy?.penalty ? (
              <p>
                Mora:{' '}
                <strong>
                  {String((policy.penalty as { base?: string }).base ?? '—')}
                  {(policy.penalty as { legacyMode?: boolean }).legacyMode ||
                  policy.policySource === 'LEGACY_DERIVED'
                    ? ' · Política histórica'
                    : ''}
                </strong>
              </p>
            ) : null}
            {policy?.policySource === 'LEGACY_DERIVED' ? (
              <p className="text-xs text-slate-500">
                Política histórica preservada
                <Hint text="Las reglas de este préstamo fueron congeladas para mantener compatibilidad con su comportamiento original." />
              </p>
            ) : null}

            {policyOpen && auditorMode && policy ? (
              <dl className="mt-3 grid gap-2 rounded-md border border-slate-100 bg-slate-50 p-3 text-xs sm:grid-cols-2">
                <div>
                  <dt className="text-slate-500">Versión</dt>
                  <dd>{String(policy.version ?? '—')}</dd>
                </div>
                <div>
                  <dt className="text-slate-500">Origen</dt>
                  <dd>{policy.policySource ?? '—'}</dd>
                </div>
                <div>
                  <dt className="text-slate-500">Capturada</dt>
                  <dd>{formatDateTimeAr(policy.capturedAt)}</dd>
                </div>
                <div>
                  <dt className="text-slate-500">Payoff</dt>
                  <dd>{policy.payoffPolicy ?? '—'}</dd>
                </div>
                <div>
                  <dt className="text-slate-500">Penalty rate</dt>
                  <dd>{String((policy.penalty as { rate?: unknown })?.rate ?? '—')}</dd>
                </div>
                <div>
                  <dt className="text-slate-500">Start rule</dt>
                  <dd>{String((policy.penalty as { startRule?: unknown })?.startRule ?? '—')}</dd>
                </div>
              </dl>
            ) : null}
          </div>
        )}
      </SectionCard>
    </div>
  );
}
